import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useScrollReveal } from "../../../hooks/useScrollReveal";
import { useNavigationContext } from "../../common/navigation/NavigationContext";
// 🎯 FIXED PATH: Import the handshake helper for the S3 ping
import { executeWorkflowHandshake } from "./Workflow-esg/Workflow_XLS_Support";

export default function WorkflowRefreshESG() {
  const navigate = useNavigate();
  const revealRef = useScrollReveal();
  const { setWorkflowContext } = useNavigationContext();

  // 📦 State to track if validated files exist in the Clean Room
  const [hasCleanInventory, setHasCleanInventory] = useState(false);

  useEffect(() => {
    setWorkflowContext(
      "Refresh ESG Taxonomy",
      "/registered/site-admin/taxonomy-workflow",
    );

    /**
     * 🕵️ S3 Inventory Check
     * Pings the clean bucket to see if Step 3 should be unfrozen.
     */
    const checkInventory = async () => {
      console.group("📡 [REFRESH_DIAGNOSTIC]: S3 Clean Room Ping");
      try {
        /** * 🎯 CORRECTED INTENT:
         * The third parameter 'intent' is now explicitly set to "Inventory"
         * to match the Backend expectation.
         */
        const response = await executeWorkflowHandshake(
          "doorman/inventory",
          { bucketAlias: "esg-staging-clean" },
          "Inventory",
        );

        console.log("📥 BFF Response Received:", response);

        // Normalize response check based on expected "Inventory" payload
        const files = response?.files || response?.data || [];

        if (files.length > 0) {
          console.log(
            `✅ SUCCESS: Found ${files.length} validated taxonomies.`,
          );
          setHasCleanInventory(true);
        } else {
          console.warn(
            "⚠️ EMPTY: No validated files found. Step 3 remains frozen.",
          );
          setHasCleanInventory(false);
        }
      } catch (err) {
        console.error(
          "🚨 FAIL: Handshake failed with 'Inventory' intent.",
          err.message,
        );
        setHasCleanInventory(false);
      } finally {
        console.groupEnd();
      }
    };

    checkInventory();
  }, [setWorkflowContext]);

  const workflowSteps = [
    {
      title: "Import ESG",
      description:
        "Upload official EFRAG Excel taxonomy linkbases (Presentation, Calculation, and Definition).",
      icon: "bi-file-earmark-excel-fill",
      actionLabel: "Upload ESG Taxonomy",
      actionTarget: "/registered/upload/monitor",
      active: true,
    },
    {
      title: "Validate and Prepare",
      description:
        "Choose the file uploaded in step 1 to build the declaration template for an ESG reporting period.",
      icon: "bi-diagram-3-fill",
      actionLabel: "Process Data",
      actionTarget: "/registered/site-admin/workflow_hub",
      active: true,
    },
    {
      title: "Reporting Templates",
      description:
        "Curate specific disclosure scopes using the generated taxonomy logic.",
      icon: "bi-collection-fill",
      actionLabel: "Manage Templates",
      actionTarget: "/registered/site-admin/workflow/template-builder/new",
      // 🎯 DYNAMIC: Enabled only if S3 has clean files
      active: hasCleanInventory,
    },
    {
      title: "Evaluation & Determination",
      description:
        "Capture inputs against templates to determine compliance and materiality scores.",
      icon: "bi-check-circle-fill",
      actionLabel: "Open Evaluator",
      actionTarget: "#",
      active: false,
    },
  ];

  return (
    <div className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section ref={revealRef} className="page-hero reveal-section">
          <h1 className="page-hero__title">Refresh ESG Taxonomy</h1>
          <p className="page-hero__lead">
            Strategic Workflow: Transforming EFRAG Source into Reporting
            Intelligence
          </p>
        </section>

        <section className="page-section px-4">
          <div className="row g-4 mb-5">
            {workflowSteps.map((step, index) => (
              <div key={index} className="col-12 col-md-6 col-lg-3">
                <div
                  className={`card h-100 border-0 shadow-sm bg-white ${!step.active ? "opacity-50" : ""}`}
                >
                  <div className="card-body p-4 d-flex flex-column text-center">
                    <div className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary rounded-circle mb-3 p-4 mx-auto">
                      <i className={`bi ${step.icon} fs-2`}></i>
                    </div>
                    <h5 className="fw-bold mb-2 small text-uppercase text-secondary">
                      Step {index + 1}
                    </h5>
                    <h6 className="fw-bold mb-3">{step.title}</h6>
                    <p className="small text-muted mb-4 flex-grow-1">
                      {step.description}
                    </p>

                    <button
                      type="button"
                      className={`btn w-100 fw-bold ${step.active ? "btn-primary shadow-sm" : "btn-outline-secondary disabled"}`}
                      disabled={!step.active}
                      onClick={() => step.active && navigate(step.actionTarget)}
                    >
                      {step.actionLabel}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-5 pt-4 border-top d-flex justify-content-between">
            <button
              type="button"
              className="btn btn-link text-decoration-none p-0 text-secondary small"
              onClick={() => navigate("/registered/site-admin")}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Return to Admin Dashboard
            </button>
            <span className="text-muted small align-self-center">
              Inventory Status:{" "}
              <strong>
                {hasCleanInventory
                  ? "Validated Data Available"
                  : "Awaiting Taxonomy Ingestion"}
              </strong>
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
